import type { AxiosInstance, AxiosRequestConfig } from 'axios';

import type { CustomErrorInterceptor } from '../../common/api/clients/localApiAxiosErrorInterceptors';

import { appConfig } from '../../../appConfig';
import { GdprCookieVendors, hasAcceptedGdprCookieVendorsSync } from '../../../utility/gdpr';
import axiosClientFactory from '../../common/api/clients/clientFactory';
import { createDefaultException } from '../../common/exceptions/exceptionUtils';
import persistance from '../../common/persistance/persistance';

const directEventsErrorAxiosInterceptor: CustomErrorInterceptor = (error) => {
    if (typeof error === 'string') {
        const defaultException = createDefaultException();
        defaultException.friendlyMessage = error;
        return defaultException;
    }

    return null;
};

/**
 * Axios client for personalization /events only.
 * Uses personalizationEventsApi* (not personalizationApi*) so api-version can differ from search.
 */
const getDirectPersonalizationEventsAxiosClient = (): AxiosInstance => {
    const acceptedConsent = hasAcceptedGdprCookieVendorsSync([GdprCookieVendors.Loop54]);

    const axiosRequestConfig: AxiosRequestConfig = {
        baseURL: appConfig.coopSettings.serviceAccess.personalizationEventsApiUrl,
        headers: {
            'Ocp-Apim-Subscription-Key':
                appConfig.coopSettings.serviceAccess.personalizationEventsApiSubscriptionKey,
        },
        params: {
            'api-version': appConfig.coopSettings.serviceAccess.personalizationEventsApiVersion,
        },
    };

    const loopUserId = persistance.loop54UserSession.get();

    if (acceptedConsent) {
        if (appConfig.coopUserSettings.userId) {
            if (axiosRequestConfig.headers) {
                axiosRequestConfig.headers['User-Id'] = appConfig.coopUserSettings.userId;
            }
        }
        if (loopUserId) {
            if (axiosRequestConfig.headers) {
                axiosRequestConfig.headers['Loop-User-Id'] = loopUserId;
            }
        }
    }

    const directEventsAxiosClient = axiosClientFactory.createDirectApiClient(axiosRequestConfig, [
        directEventsErrorAxiosInterceptor,
    ]);

    directEventsAxiosClient.interceptors.response.use((response) => {
        if (acceptedConsent) {
            const headerId = response.headers['loop-user-id'];
            if (headerId) {
                persistance.loop54UserSession.set(headerId);
            }
        }

        return response;
    });

    return directEventsAxiosClient;
};

export default getDirectPersonalizationEventsAxiosClient;
